import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { sessionAbsent, sessionExpired, sessionLoaded, signedOut } from '@/store/authSlice';
import {
  ApiError,
  callApi,
  setPasswordChangeRequiredHandler,
  setSessionExpiredHandler,
} from '@/lib/apiClient';
import { api } from '@/store/api';
import { clearCaches } from '@/pwa/useServiceWorker';
import { desktopApi } from '@/features/workSessions/useDesktop';
import {
  requestBrowserNotificationPermission,
  welcomeAfterLogin,
} from '@/features/notifications/browserNotifications';

/**
 * Session lifecycle.
 *
 * `useAuthBootstrap` runs once at the top of the app and asks the server who we are.
 * Until it answers, auth status is `unknown` and the guards hold — redirecting during
 * that window would bounce every page refresh to the login screen before the check
 * completes, which is the classic "logged in but keeps logging me out" bug.
 */

export function useAuthBootstrap(): void {
  const dispatch = useAppDispatch();

  useEffect(() => {
    // The client calls this when a refresh fails, so a dead session clears state
    // instead of leaving the user on a populated screen that silently 401s.
    setSessionExpiredHandler(() => {
      dispatch(sessionExpired());
      dispatch(api.util.resetApiState());
    });

    setPasswordChangeRequiredHandler(() => {
      void (async () => {
        try {
          const res = await callApi('auth.me');
          dispatch(sessionLoaded(res.data));
        } catch {
          dispatch(sessionExpired());
          dispatch(api.util.resetApiState());
        }
      })();
    });

    let cancelled = false;

    void (async () => {
      try {
        const res = await callApi('auth.me');
        if (!cancelled) dispatch(sessionLoaded(res.data));
      } catch {
        // A 401 here is the normal "not signed in" case, not an error worth surfacing.
        if (!cancelled) dispatch(sessionAbsent());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);
}

export function useAuthActions() {
  const dispatch = useAppDispatch();

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await callApi('auth.login', { body: { email, password } });
      // Nothing cached may survive a user switch: on a shared machine the previous
      // person's directory and team data would otherwise still be in the store.
      dispatch(api.util.resetApiState());
      dispatch(sessionLoaded(res.data));

      void (async () => {
        const permission = await requestBrowserNotificationPermission();
        if (permission === 'granted') welcomeAfterLogin(res.data);
      })();

      // The desktop app re-reads the server's session state rather than assuming it.
      await desktopApi()?.signedIn();
      return res.data;
    },
    [dispatch],
  );

  const logout = useCallback(
    async (allDevices = false) => {
      try {
        await callApi('auth.logout', { body: { allDevices } });
      } catch {
        // Even if the call fails the local session must end — leaving the user
        // apparently signed in after they pressed Sign out is worse than a lost
        // server-side revocation they can retry.
      }
      dispatch(signedOut());
      dispatch(api.util.resetApiState());

      /*
       * The Redux store is not the only place data survives a sign-out.
       *
       * A service-worker cache lives on the origin, outlives the session and is
       * readable by any script on the page — so on a shared machine the next person's
       * first load could come from the last person's cache. No `/api` response is
       * cached at all (the service worker answers them `NetworkOnly`), and clearing
       * the shell caches as well costs one reload and removes the question entirely.
       */
      await clearCaches();

      // The desktop app stops any running work session and clears its own queue.
      await desktopApi()?.signedOut();
    },
    [dispatch],
  );

  const activate = useCallback(
    async (token: string, password: string) => {
      const res = await callApi('auth.activate', { body: { token, password } });
      dispatch(sessionLoaded(res.data));
      return res.data;
    },
    [dispatch],
  );

  const resetPassword = useCallback(
    async (token: string, password: string) => {
      const res = await callApi('auth.resetPassword', { body: { token, password } });
      dispatch(sessionLoaded(res.data));
      return res.data;
    },
    [dispatch],
  );

  /**
   * Re-read the session into the store.
   *
   * Needed after anything that changes what the SERVER thinks of this user —
   * `mustChangePassword` clearing, a role change. Reading `/auth/me` without
   * dispatching leaves the guards acting on the old session, which sends the user
   * straight back to the screen they just finished.
   */
  const reloadSession = useCallback(async () => {
    const res = await callApi('auth.me');
    dispatch(sessionLoaded(res.data));
    return res.data;
  }, [dispatch]);

  return { login, logout, activate, resetPassword, reloadSession };
}

export function useAuthState() {
  return useAppSelector((s) => s.auth);
}

/** Turns an ApiError into the sentence a user should actually read. */
export function authErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return 'Could not reach the server. Check your connection and try again.';
  }

  switch (err.code) {
    case 'INVALID_CREDENTIALS':
      return 'Email or password is incorrect.';
    case 'ACCOUNT_LOCKED':
      return 'Too many failed attempts. Please wait a few minutes and try again.';
    case 'ACCOUNT_INACTIVE':
      return 'This account is not active. Contact your administrator.';
    case 'NOT_FOUND':
      return 'This link is invalid or has expired. Request a new one.';
    case 'RATE_LIMITED':
      return 'Too many requests. Please wait a moment and try again.';
    default:
      return err.message;
  }
}
